import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { GoogleAd } from './GoogleAd';
import { trackEvent } from '@/lib/analytics';

interface AdSlotProps {
  placement: 'pre-game' | 'result';
  adSlot?: string;
  showRemoveAdsOption?: boolean;
}

export function AdSlot({ placement, adSlot = '', showRemoveAdsOption = true }: AdSlotProps) {
  const { profile, entitlement } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Check if user should see ads
  const shouldShowAds = !profile?.is_verified && !entitlement?.remove_ads;

  // Don't render anything for verified/paid users
  if (!shouldShowAds) {
    return null;
  }

  const handleRemoveAds = async () => {
    trackEvent.removeAdsClicked();
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <GoogleAd 
        adSlot={adSlot} 
        adFormat={placement === 'pre-game' ? 'horizontal' : 'rectangle'} 
      />
      
      {showRemoveAdsOption && (
        <div className="text-center pt-2">
          <button 
            onClick={handleRemoveAds}
            disabled={isLoading}
            className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            {isLoading ? 'Loading...' : 'Remove ads · $4.99'}
          </button>
        </div>
      )}
    </div>
  );
}
